import { executeCommand } from './SendCommandToACC';

/**
 * The one ACC client core. Every command answers the envelope
 * `{ success, data }` or `{ success: false, error, errorCode, details }`, as
 * an object or a JSON string; `run` parses it and throws on failure so
 * callers use try/catch. Every thrown error carries `code` (server errorCode
 * or null), `details` (server details or null) and `isNetwork`.
 *
 * Only transport failures are `isNetwork`: the request never got an answer,
 * or the gateway or Lambda fell over. A failure envelope, a 4xx, or an
 * unparseable 200 body (the Lambda's `Internal Server Error: ...` string) is
 * a definitive answer from the server. Two of those answers come from a
 * backend that does not know the feature yet and are typed so a page can
 * show one quiet line instead of the raw body: a bare 403 string is
 * `E_NOT_ALLOWED`, an `Unknown command: x` string is `E_UNKNOWN_COMMAND`.
 */

export const NOT_ALLOWED_COPY = 'You do not have access to this yet.';
export const UNKNOWN_COMMAND_COPY = 'This is not available yet.';

const UNKNOWN_COMMAND_RE = /^\s*unknown command\b/i;

export const parseEnvelope = (raw) => {
    if (raw && typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

export const commandError = (message, { code = null, details = null, isNetwork = false, cause = null } = {}) => {
    const error = new Error(message);
    error.code = code || null;
    error.details = details || null;
    error.isNetwork = isNetwork;
    if (cause) error.cause = cause;
    return error;
};

/** Transport failure: the request never got an answer, or the gateway/Lambda fell over. */
export const isNetworkError = (error) => {
    if (!error || typeof error !== 'object') return false;
    if (error.isNetwork === true) return true;
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
    if (error.response) {
        const status = Number(error.response.status);
        return status >= 500 || status === 429;
    }
    return Boolean(error.isAxiosError || error.request);
};

/** The backend does not know this feature yet: a page shows one quiet line, not an alert. */
export const isUnavailableError = (error) => error?.code === 'E_NOT_ALLOWED' || error?.code === 'E_UNKNOWN_COMMAND';

/** A non-envelope string body the Lambda answers with, typed when it is one of the two rollout answers. */
const stringBodyError = (text, cause) => {
    if (UNKNOWN_COMMAND_RE.test(text)) return commandError(UNKNOWN_COMMAND_COPY, { code: 'E_UNKNOWN_COMMAND', cause });
    if (cause?.response?.status === 403) return commandError(NOT_ALLOWED_COPY, { code: 'E_NOT_ALLOWED', cause });
    return commandError(text, { cause });
};

export const wrapTransportError = (error) => {
    if (isNetworkError(error)) {
        return commandError(error?.message || 'Network error.', { isNetwork: true, cause: error });
    }
    // A 4xx from the Lambda (401/403) says why in the body, as a plain string or
    // an envelope; that beats axios's "Request failed with status code N".
    const body = error?.response?.data;
    const envelope = parseEnvelope(body);
    if (envelope && envelope.error) {
        return commandError(envelope.error, { code: envelope.errorCode, details: envelope.details, cause: error });
    }
    if (typeof body === 'string' && body.trim()) return stringBodyError(body.trim(), error);
    return commandError(error?.message || 'Request did not go through.', { cause: error });
};

/** `run` over a dispatcher; the feature clients hand in the one that branches to their mock store. */
export const runWith = (dispatch) => async (command, args = []) => {
    let raw;
    try {
        raw = await dispatch(command, args);
    } catch (error) {
        throw wrapTransportError(error);
    }
    const envelope = parseEnvelope(raw);
    if (!envelope) {
        throw typeof raw === 'string' ? stringBodyError(raw, null) : commandError('Unexpected response from server.');
    }
    if (!envelope.success) {
        throw commandError(envelope.error || 'Request did not go through.', {
            code: envelope.errorCode,
            details: envelope.details,
        });
    }
    return envelope.data;
};

export const run = runWith(executeCommand);
